import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserHistory } from 'history';
import { configure as mobxConfigure } from 'mobx';

import { configure as httpConfigure } from 'services/base/http';
import { configure as systemConfigure } from 'services/base/config';
import { setupWebNotifications } from 'services/base/notifications';

import { FCMMessage } from 'services/base/ipc_types';
import { popPaymentRedirect } from 'services/base/redirect';
import { Optional } from 'services/base/types';

import { createApplication } from 'app/create';

type AppConfiguration = {
  basename: string;
  apiHost: string;
  platform: string;
  version: string;
  fcmToken: Optional<string>;
  initialNotification: Optional<FCMMessage>;
};

declare global {
  interface Window {
    main: (configuration: AppConfiguration) => void;
  }
}

// I don't know why, but when embedded in ReactNative, injected Javascript can run multiple times it seems
let MAIN_HAS_RUN = false;
window.main = ({ apiHost, basename, platform, version, fcmToken, initialNotification }: AppConfiguration) => {
  if (MAIN_HAS_RUN) {
    return;
  }
  MAIN_HAS_RUN = true;
  const enforceActions = process.env.REACT_APP_MOBX_ENFORCE_ACTIONS === 'observed' ? 'observed' : 'never';
  const history = createBrowserHistory({ basename });
  const paymentRedirect = popPaymentRedirect();
  mobxConfigure({ enforceActions });
  httpConfigure(apiHost);
  systemConfigure({
    history,
    platform,
    version,
    fcmToken: fcmToken ?? undefined,
    initialNotification: initialNotification ?? undefined,
    paymentRedirect: paymentRedirect ?? undefined,
  });

  const Application = createApplication(history);
  const container = document.getElementById('root');
  if (container != null) {
    createRoot(container).render(<Application/>);
  }
};

if (process.env.REACT_APP_EMBEDDED !== 'true') {
  if (process.env.REACT_APP_API_HOST != null && process.env.REACT_APP_VERSION != null) {
    const apiHost = process.env.REACT_APP_API_HOST;
    const version = process.env.REACT_APP_VERSION;
    void (() => {
      setupWebNotifications();
      window.main({
        apiHost,
        basename: '/',
        version,
        platform: 'web',
        fcmToken: window.WebNotifications?.token,
        initialNotification: window.WebNotifications?.initial,
      });
    })();
  } else {
    /* eslint-disable-next-line no-alert */
    alert('Please define REACT_APP_API_HOST and REACT_APP_VERSION');
  }
}
